import React, { useState, useCallback } from 'react';
import { t } from 'i18next';
import { useFormContext } from 'react-hook-form';
import { DeepPartial } from 'ts-essentials';
import { PictureSpot } from '@sparted/shared-library/business/types';

import { BaseContentFormValues } from 'Pages/Content/ContentEdition/contentForm.hook';
import { COLORS } from 'Components/foundation';
import MModal from 'Components/modal/MModal/MModal';
import UTextLink from 'Components/unit/UTextLink/UTextLink';
import STextArea from 'Components/structural/STextArea/STextArea';
import { Languages } from 'Models/Enum';

import { ContentValidationError } from 'Store/entities/content/content.types';
import { translateApiErrors } from 'Services/api-error/api-error.service';

import { PICTURE_SPOT_DESCRIPTION_LIMIT, QUESTION_CHAR_LIMIT } from '../gameplay.constants';

import generalStyles, { CONTENT_TEXT_AREA_WRAPPER_RADIUS } from '../gameplay.style';
import styles from '../PictureSpotForm.style';

type SwitchQuestionProps = {
  style?: React.CSSProperties;
  errors: ContentValidationError;
  language: keyof typeof Languages;
  question: string;
  description: string;
  currentTab: number;
  tabs: DeepPartial<PictureSpot['items']>;
  onChangeQuestion: (text: string) => void;
  onChangeDescription: (text: string) => void;
  setCurrentTab: (tab: number) => void;
};

export const SwitchQuestion = ({
  errors,
  language,
  style,
  question,
  currentTab,
  tabs = [],
  description,
  onChangeQuestion,
  onChangeDescription,
  setCurrentTab,
}: SwitchQuestionProps) => {
  const { watch, setValue } = useFormContext<BaseContentFormValues & { game: DeepPartial<PictureSpot> }>();
  const [visible, setVisible] = useState(false);

  const handleDeleteCurrentTab = useCallback(() => {
    const previousItems = watch('game.items');

    const newItems = previousItems?.filter((_, index) => index !== currentTab);
    setValue('game.items', newItems);
    setCurrentTab((watch('game.items')?.length ?? 1) - 1);
    setVisible(false);
  }, [currentTab, tabs, setCurrentTab]);

  const modalTitle = `${t('gameplays:picture_spot.delete_question')} ${currentTab + 1}`;
  const descriptionErrors = errors?.[language]?.game?.items?.[currentTab + 1]?.description;
  const questionErrors = errors?.[language]?.game?.items?.[currentTab + 1]?.question;

  return (
    <div style={style}>
      <MModal
        labelActionButton={t('gameplays:picture_spot.delete_question')}
        labelSecondButton={t('gameplays:change_gameplay.cancel')}
        onCloseModal={() => setVisible(false)}
        onAction={handleDeleteCurrentTab}
        onSecondAction={() => setVisible(false)}
        actionButtonType="destructive"
        title={modalTitle}
        visible={visible}
        style={styles.modal}
        dataTestContext="switch-question-delete-question"
      >
        <p style={styles.deleteQuestionDescription}>{t('gameplays:picture_spot.delete_question_description')}</p>
      </MModal>
      <UTextLink
        colorOverride={COLORS.TEXT.SECONDARY_DEFAULT}
        disabled={tabs.length === 1}
        mode="small"
        text={`${t('gameplays:picture_spot:delete_question')} ${currentTab + 1}`}
        onClick={() => setVisible(true)}
        leftIcon="delete"
        style={styles.deleteQuestion}
      />
      <STextArea
        textAreaTypo="BODY2"
        radiusWrapper={CONTENT_TEXT_AREA_WRAPPER_RADIUS}
        maxLength={PICTURE_SPOT_DESCRIPTION_LIMIT}
        textAreaStyle={styles.textAreaBackground}
        label={t('gameplays:picture_spot.image_description')}
        labelSize="L"
        placeholder={t('gameplays:picture_spot.enter_description')}
        style={generalStyles.section}
        value={description}
        onChange={(text) => onChangeDescription(text)}
        errorMessage={translateApiErrors(descriptionErrors ? { description: descriptionErrors } : {})}
        dataTestContext="picture-spot-image-description"
      />
      <STextArea
        textAreaTypo="BODY2"
        radiusWrapper={CONTENT_TEXT_AREA_WRAPPER_RADIUS}
        maxLength={QUESTION_CHAR_LIMIT}
        textAreaStyle={styles.textAreaBackground}
        placeholder={t('gameplays:picture_spot.add_a_question')}
        label={t('gameplays:picture_spot.question')}
        labelSize="L"
        style={generalStyles.section}
        value={question}
        onChange={(text) => onChangeQuestion(text)}
        errorMessage={translateApiErrors(questionErrors ? { question: questionErrors } : {})}
        dataTestContext="picture-spot-image-question"
      />
    </div>
  );
};

export default SwitchQuestion;
